import { ContractModelTypes } from '@mono/proto-clients';
import { useQueries, useQuery } from '@tanstack/react-query';
import { contractModelClient, deliveryClient } from '../../../lib/connect/clients';
import { cmKeys, OccurrenceWindow, useOccurrencesFanOut } from '../Deliverables/hooks';
import { onTimePercent } from '../Deliverables/lib/status';
// Phase 5b — the command center reads the cross-project rollups shipped by 5a
// (PR #742) through ryugu's DeliveryService, via sabaody. Two RPCs back it:
//   - getDeliveryKPIRollups → per-portfolio + per-sector delivery-truth metrics
//   - getPortfolioDashboard → company-wide totals + the per-program health grid
// Both are tenant-gated on `main`; per-program viewer projection (drop/mask of
// unauthorized programs, gated grand totals) is enforced server-side by 5d
// (PR #744) once RYUGU_VIEWMODE_ENFORCE is on — the UI renders what it gets.
const KEY = 'command-center';
// 10 s ceiling on every command-center RPC. Without a deadline, a hanging
// ryugu/sabaody response keeps isLoading: true forever — the page never
// leaves its skeleton state. AbortSignal.any merges the query's own signal
// (fires on unmount/query-cancel) with the deadline so whichever fires first
// wins, and React Query transitions the query to isError.
const RPC_TIMEOUT_MS = 10000;
function withTimeout(signal) {
    return AbortSignal.any([
        signal,
        AbortSignal.timeout(RPC_TIMEOUT_MS)
    ]);
}
export function useDeliveryRollups(filter) {
    return useQuery({
        queryKey: [
            KEY,
            'rollups',
            filter
        ],
        queryFn: async ({ signal })=>{
            const resp = await deliveryClient.getDeliveryKPIRollups({
                companyId: filter.companyId,
                sectorId: filter.sectorId ?? '',
                businessUnitId: filter.businessUnitId ?? '',
                portfolioId: filter.portfolioId ?? '',
                contractState: filter.contractState ?? ''
            }, {
                signal: withTimeout(signal)
            });
            return {
                portfolioRollups: resp.portfolioRollups,
                sectorRollups: resp.sectorRollups,
                // DRIFT-2: programs the viewer can't see (NoView) are dropped from
                // every rollup server-side; this is how many, surfaced as "N hidden".
                hiddenCount: resp.hiddenCount
            };
        },
        enabled: !!filter.companyId,
        staleTime: 30000,
        retry: 1
    });
}
export function usePortfolioDashboard(filter) {
    return useQuery({
        queryKey: [
            KEY,
            'dashboard',
            filter.companyId,
            filter.sectorId,
            filter.businessUnitId,
            filter.portfolioId,
            filter.contractState
        ],
        queryFn: async ({ signal })=>{
            return deliveryClient.getPortfolioDashboard({
                companyId: filter.companyId,
                sectorId: filter.sectorId ?? '',
                businessUnitId: filter.businessUnitId ?? '',
                portfolioId: filter.portfolioId ?? '',
                contractState: filter.contractState ?? ''
            }, {
                signal: withTimeout(signal)
            });
        },
        enabled: !!filter.companyId,
        staleTime: 30000,
        retry: 1
    });
}
export function countsOf(r) {
    return {
        stateCounts: {
            ...r.stateCounts
        },
        approved: r.onTime?.approved ?? 0,
        timingKnown: r.onTime?.timingKnown ?? 0,
        onTime: r.onTime?.onTime ?? 0,
        total: r.total
    };
}
export function sumCounts(all) {
    const out = {
        stateCounts: {},
        approved: 0,
        timingKnown: 0,
        onTime: 0,
        total: 0
    };
    for (const c of all){
        for (const [state, n] of Object.entries(c.stateCounts)){
            out.stateCounts[state] = (out.stateCounts[state] ?? 0) + n;
        }
        out.approved += c.approved;
        out.timingKnown += c.timingKnown;
        out.onTime += c.onTime;
        out.total += c.total;
    }
    return out;
}
/** Rounded on-time share; null when no approval has known timing ("No approvals yet"). */ export function countsOnTimePercent(c) {
    return onTimePercent(new ContractModelTypes.OnTimeCounts({
        approved: c.approved,
        timingKnown: c.timingKnown,
        onTime: c.onTime
    }));
}
/** The KPI basis of the portfolio roll-ups (D104): each program's current contract period. */ export const CURRENT_PERIOD_COUNTS = {
    window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
    pageSize: 1
};
/** One ListOccurrences per program: per-program counts plus their sum. */ export function useDeliverableCounts(programIds, req) {
    const results = useOccurrencesFanOut(programIds, req);
    // A failed refetch keeps its last good data; such a program is reported as
    // failed, so it must not be summed too (the partial-sum note says it is left out).
    const failedIds = new Set(programIds.filter((_, i)=>results[i]?.isError));
    const byProgram = new Map();
    programIds.forEach((id, i)=>{
        const d = results[i]?.data;
        if (d && !failedIds.has(id)) byProgram.set(id, countsOf(d));
    });
    return {
        byProgram,
        total: sumCounts(byProgram.values()),
        isLoading: results.some((q)=>q.isLoading),
        isError: programIds.length > 0 && failedIds.size === programIds.length,
        failed: failedIds.size,
        failedIds
    };
}
/**
 * Every listed program's deliverables (ListDuties), for naming the dates a
 * portfolio page lists: dutyId → deliverable. Keyed like useDuties, so each
 * program's slice is shared with its own pages' cache.
 */ export function useDutiesFanOut(programIds) {
    const results = useQueries({
        queries: programIds.map((programId)=>({
                queryKey: cmKeys.duties(programId, false),
                enabled: !!programId,
                staleTime: 15000,
                queryFn: async ()=>{
                    const resp = await contractModelClient.listDuties({
                        body: {
                            programId,
                            includeRules: false
                        }
                    });
                    return resp.body ?? new ContractModelTypes.ListDutiesResponse();
                }
            }))
    });
    const byId = new Map();
    for (const q of results)for (const d of q.data?.duties ?? [])byId.set(d.id, d);
    return byId;
}
